import { useState, useEffect } from "react";
import { puedeCrear } from "../auth/permisos";
import DateInput from "../../shared/components/DateInput";

import { avisarError } from "../../shared/errores";
import { directorioMiembros, eliminar as eliminarFila, guardar as guardarFila, listar, unir } from "../../services/datos";
import { avisar, confirmar } from "../../shared/avisos";
import Selector from "../../shared/components/Selector";
import Esqueleto from "../../shared/components/Esqueleto";

const camposIniciales = {
  miembro_id: "",
  fecha_baja: "",
  motivo: "",
  detalle: ""
};

function FormularioBaja({ inicial, miembros, onGuardar, onCancelar }) {
  const [form, setForm] = useState(inicial ? { ...inicial, miembro_id: inicial.miembro_id ? String(inicial.miembro_id) : "" } : camposIniciales);
  const input = "campo";
  const label = "etiqueta";

  useEffect(() => {
    if (inicial) {
      setForm({ ...inicial, miembro_id: inicial.miembro_id ? String(inicial.miembro_id) : "" });
    } else {
      setForm(camposIniciales);
    }
  }, [inicial]);

  const handleSubmit = () => {
    if (!form.miembro_id || !form.fecha_baja || !form.motivo) {
      avisar("Miembro, fecha y razón son obligatorios");
      return;
    }
    onGuardar(form);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto bg-black/80 p-4 backdrop-blur-sm ventana-fondo">
      <div className="w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-2xl border border-white/10 bg-zinc-950 p-6 sm:p-8 ventana-panel">
        <h3 className="titulo-ventana mb-5">{inicial ? "Editar Baja" : "Registrar Baja"}</h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className={label}>Miembro *</label>
            <Selector
              className={input}
              value={form.miembro_id}
              onChange={(e) => setForm({ ...form, miembro_id: e.target.value })}
            >
              <option value="">Seleccionar miembro...</option>
              {miembros.map((m) => (
                <option key={m.id} value={m.id}>{m.nombres} {m.apellidos}</option>
              ))}
            </Selector>
          </div>
          <DateInput
            label="Fecha de baja *"
            className={input}
            value={form.fecha_baja}
            onChange={(e) => setForm({ ...form, fecha_baja: e.target.value })}
          />
          <div className="md:col-span-2">
            <label className={label}>Razón *</label>
            <input
              className={input}
              value={form.motivo || ""}
              onChange={(e) => setForm({ ...form, motivo: e.target.value })}
              placeholder="Motivo de la baja"
            />
          </div>
          <div className="md:col-span-2">
            <label className={label}>Notas</label>
            <textarea
              className={input}
              rows={4}
              value={form.detalle || ""}
              onChange={(e) => setForm({ ...form, detalle: e.target.value })}
              placeholder="Detalles adicionales"
            />
          </div>
        </div>

        <div className="flex gap-3 mt-6">
          <button onClick={handleSubmit} className="flex-1 btn-primario">Guardar baja</button>
          <button onClick={onCancelar} className="flex-1 btn-fantasma">Cancelar</button>
        </div>
      </div>
    </div>
  );
}

function Bajas({ usuario }) {
  const [bajas, setBajas] = useState([]);
  const [miembros, setMiembros] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [buscar, setBuscar] = useState("");
  const [mostrarForm, setMostrarForm] = useState(false);
  const [bajaEditar, setBajaEditar] = useState(null);

  const cargar = () => {
    setCargando(true);
    Promise.all([
      listar("bajas", { orden: "fecha_baja", descendente: true }),
      directorioMiembros(),
    ])
      .then(([bajasData, miembrosData]) => {
        setBajas(unir(bajasData, "miembro_id", miembrosData, "miembros"));
        setMiembros(miembrosData);
        setCargando(false);
      })
      .catch((e) => { setCargando(false); avisarError(e); });
  };

  useEffect(() => { cargar(); }, []);

  const guardar = (form) => {
    guardarFila("bajas", bajaEditar?.id, form)
      .then(() => {
        setMostrarForm(false);
        setBajaEditar(null);
        cargar();
      })
      .catch((e) => avisar(e.message, "error"));
  };

  const eliminar = async (id) => {
    if (!(await confirmar("¿Eliminar esta baja?", { aceptar: "Eliminar" }))) return;
    eliminarFila("bajas", id).then(() => cargar()).catch((e) => avisar(e.message, "error"));
  };

  const filtradas = bajas.filter((b) =>
    `${b.miembros?.nombres || ""} ${b.miembros?.apellidos || ""} ${b.motivo || ""}`.toLowerCase().includes(buscar.toLowerCase())
  );

  return (
    <div className="p-6">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between mb-6">
        <div>
          <h2 className="titulo-pagina">Bajas</h2>
          <p className="text-gray-400 text-sm mt-1">Registros de miembros que dejaron de asistir</p>
        </div>
        {puedeCrear(usuario) ? (
          <button
            onClick={() => { setBajaEditar(null); setMostrarForm(true); }}
            className="btn-primario"
          >
            Registrar baja
          </button>
        ) : (
          <span className="text-sm text-gray-400">Solo pastor o administrador puede crear</span>
        )}
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-[280px_minmax(0,1fr)] gap-6 mb-6">
        <div className="panel p-4">
          <p className="text-gray-400 text-sm">Total de bajas</p>
          <p className="text-white font-black text-4xl mt-2">{bajas.length}</p>
          <input
            value={buscar}
            onChange={(e) => setBuscar(e.target.value)}
            placeholder="Buscar miembro o razón..."
            className="mt-4 campo"
          />
        </div>

        <div className="panel p-4 max-h-[72vh] overflow-y-auto">
          {cargando ? (
            <Esqueleto texto="Cargando bajas…" />
          ) : filtradas.length === 0 ? (
            <p className="text-gray-400">No se encontraron resultados.</p>
          ) : (
            <div className="space-y-3">
              {filtradas.map((b) => (
                <div key={b.id} className="rounded-2xl border border-gray-700 p-4 bg-oscuro">
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <p className="text-white text-lg font-semibold">{b.miembros?.nombres} {b.miembros?.apellidos}</p>
                      <p className="text-gray-400 text-sm">{b.fecha_baja} · {b.motivo}</p>
                    </div>
                    <button onClick={() => eliminar(b.id)} className="btn-peligro btn-sm">Eliminar</button>
                  </div>
                  {b.detalle && <p className="text-gray-500 text-sm mt-3">{b.detalle}</p>}
                  <button
                    onClick={() => { setBajaEditar(b); setMostrarForm(true); }}
                    className="mt-4 btn-fantasma btn-sm"
                  >
                    Editar
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {mostrarForm && (
        <FormularioBaja
          inicial={bajaEditar}
          miembros={miembros}
          onGuardar={guardar}
          onCancelar={() => { setMostrarForm(false); setBajaEditar(null); }}
        />
      )}
    </div>
  );
}

export default Bajas;
